import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { http } from '../api';

// Public attorney portal — tokenised URL, no login. Instructing attorneys
// land here to see matter status + outstanding invoices.

type PortalData = {
  matter: { id: number; reference: string; name: string; case_number?: string; status: string; opened_at: string };
  stage: { code: string; name: string } | null;
  court: { name: string; tier: string; seat?: string } | null;
  parties: { role: string; side: string; name: string }[];
  upcoming_deadlines: { ROWID: number; kind: string; title: string; starts_at: string; severity: string; statutory_reference?: string }[];
  recent_events: { event_type: string; summary: string; occurred_at: string }[];
  invoices: { ROWID: number; number: string; kind: string; status: string; issue_date?: string; due_date?: string; total_zar: number; amount_paid_zar: number }[];
  attorney: { name: string; email: string } | null;
};

export default function Portal() {
  const [params] = useSearchParams();
  const token = params.get('t') || '';
  const [data, setData] = useState<PortalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setError('Missing portal token.'); setLoading(false); return; }
    http.get<PortalData>(`/portal?t=${encodeURIComponent(token)}`)
      .then(setData)
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div style={page}><div className="muted">Loading\u2026</div></div>;
  if (error)   return <div style={page}><div className="card" style={{ borderColor: 'var(--color-danger)' }}><b>{error}</b><p className="muted" style={{ fontSize: 13, marginTop: 6 }}>Ask counsel for a fresh portal link.</p></div></div>;
  if (!data)   return null;

  const m = data.matter;
  return (
    <div style={page}>
      <div style={{ marginBottom: 24 }}>
        <img src="/app/litigate-logo.png" alt="Litigate" style={{ height: 32 }} />
      </div>

      <div style={{ marginBottom: 18 }}>
        <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Matter</div>
        <h1 style={{ fontSize: 28, marginTop: 4 }}>{m.reference} \u2014 {m.name}</h1>
        <div className="muted" style={{ fontSize: 13 }}>
          {m.case_number ? `Case no. ${m.case_number} \u00b7 ` : ''}
          Opened {m.opened_at} \u00b7 Status: <b style={{ color: 'var(--color-text)' }}>{m.status}</b>
          {data.stage && <> \u00b7 Stage: <b style={{ color: 'var(--color-text)' }}>{data.stage.name}</b></>}
        </div>
        {data.court && (
          <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
            Court: {data.court.name}{data.court.seat ? ` \u2014 ${data.court.seat}` : ''}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
        <div className="card">
          <h3 style={{ margin: 0, marginBottom: 10 }}>Parties</h3>
          {data.parties.length === 0 ? <p className="muted">No parties recorded yet.</p> : (
            <table>
              <tbody>
                {data.parties.map((p, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500 }}>{p.role}</td>
                    <td>{p.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <h3 style={{ margin: 0, marginBottom: 10 }}>Upcoming deadlines</h3>
          {data.upcoming_deadlines.length === 0 ? <p className="muted">Nothing scheduled.</p> : (
            <div className="stack" style={{ gap: 10 }}>
              {data.upcoming_deadlines.map((d) => (
                <div key={d.ROWID} style={{ display: 'flex', gap: 10 }}>
                  <div style={{
                    width: 4, alignSelf: 'stretch',
                    background: d.severity === 'statutory' || d.severity === 'critical' ? 'var(--color-danger)' : 'var(--color-accent)',
                    borderRadius: 2,
                  }} />
                  <div>
                    <div style={{ fontWeight: 500 }}>{d.title}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{d.starts_at?.slice(0, 10)}{d.statutory_reference ? ` \u00b7 ${d.statutory_reference}` : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ margin: 0, marginBottom: 10 }}>Invoices</h3>
        {data.invoices.length === 0 ? <p className="muted">No invoices addressed to you on this matter yet.</p> : (
          <table>
            <thead>
              <tr><th>Number</th><th>Status</th><th>Issued</th><th>Due</th><th style={{ textAlign: 'right' }}>Total</th><th style={{ textAlign: 'right' }}>Outstanding</th></tr>
            </thead>
            <tbody>
              {data.invoices.map((i) => {
                const out = Number(i.total_zar) - Number(i.amount_paid_zar || 0);
                return (
                  <tr key={i.ROWID}>
                    <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{i.number}</td>
                    <td><span className="pill">{i.status.replace('_', ' ')}</span></td>
                    <td>{i.issue_date || '\u2014'}</td>
                    <td>{i.due_date || '\u2014'}</td>
                    <td style={{ textAlign: 'right' }}>R {Number(i.total_zar).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</td>
                    <td style={{ textAlign: 'right', color: out > 0 ? 'var(--color-danger)' : 'var(--color-text-muted)' }}>
                      R {out.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ margin: 0, marginBottom: 10 }}>Recent activity</h3>
        {data.recent_events.length === 0 ? <p className="muted">No events logged yet.</p> : (
          <div className="stack" style={{ gap: 6 }}>
            {data.recent_events.map((e, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, fontSize: 13 }}>
                <div className="muted" style={{ width: 100, fontSize: 12 }}>{e.occurred_at?.slice(0, 10)}</div>
                <div style={{ flex: 1 }}><b>{e.event_type.replace('_', ' ')}</b> \u2014 <span>{e.summary}</span></div>
              </div>
            ))}
          </div>
        )}
      </div>

      {data.attorney && (
        <div className="muted" style={{ marginTop: 20, fontSize: 12 }}>
          Portal access for {data.attorney.name} &lt;{data.attorney.email}&gt;. Link is revokable by counsel at any time.
        </div>
      )}
    </div>
  );
}

const page: React.CSSProperties = {
  maxWidth: 960,
  margin: '40px auto',
  padding: '0 20px',
};
